function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useMemo, useRef, useState } from 'react';
import { arrow, autoUpdate, flip, hide, offset, shift, useFloating, useClick, useDismiss, useInteractions } from '@floating-ui/react';
import { endOfDay } from 'date-fns';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { DATE_PICKER_VARIANTS } from '../consts';
import { getToday, isPropControlled } from '../helpers';
const useSharedDateLogic = _ref => {
  var _middlewareData$arrow5, _middlewareData$arrow6;
  let {
    minDate: propsMinDate,
    maxDate: propsMaxDate,
    disablePastDates,
    disableFutureDates,
    timeZone,
    disabled,
    open: propsOverlayOpen,
    initialOpen,
    target,
    onHide,
    variant = DATE_PICKER_VARIANTS.FIELD,
    flipArgs,
    shiftArgs
  } = _ref;
  const {
    T,
    locale
  } = useLocale(_ref2 => {
    let {
      locale: loc
    } = _ref2;
    return require("../i18n/".concat(loc, ".json"));
  });
  const [open, setOpen] = useState(initialOpen);
  const arrowRef = useRef(null);
  const today = getToday(timeZone);
  const minDate = propsMinDate || (disablePastDates ? today : null);
  const maxDate = propsMaxDate || (disableFutureDates ? endOfDay(today) : null);
  const [referenceElement, setReferenceElement] = useState(target === null || target === void 0 ? void 0 : target.current);
  const {
    refs,
    context,
    middlewareData,
    placement,
    floatingStyles
  } = useFloating({
    open,
    elements: {
      reference: referenceElement
    },
    onOpenChange: x => {
      if (!x) {
        onHide === null || onHide === void 0 ? void 0 : onHide();
      }
      setOpen(x);
    },
    placement: variant === DATE_PICKER_VARIANTS.ICON ? 'bottom' : 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(variant === DATE_PICKER_VARIANTS.ICON ? 10 : 0), flip(flipArgs), shift(shiftArgs), arrow({
      element: arrowRef
    }), hide({
      strategy: 'referenceHidden'
    })]
  });
  const click = useClick(context, {
    enabled: !disabled
  });
  const dismiss = useDismiss(context);
  const interactions = useInteractions([click, dismiss]);
  const referenceRef = useCallback(node => {
    if (target) {
      target.current = node;
    }
    setReferenceElement(node);
  }, [target]);
  const toggleOverlay = useCallback(() => {
    if (open) {
      onHide === null || onHide === void 0 ? void 0 : onHide();
    }
    setOpen(!open);
  }, [open, onHide]);

  // executes when selecting a single date, or the end date in a range
  const onFinalSelection = useCallback(() => {
    if (!isPropControlled(propsOverlayOpen) && context.open) {
      toggleOverlay();
    }
  }, [context.open, propsOverlayOpen, toggleOverlay]);
  const arrowStyles = useMemo(() => {
    var _middlewareData$arrow, _middlewareData$arrow2, _middlewareData$arrow3, _middlewareData$arrow4;
    return {
      transform: "translate(".concat(Math.round((_middlewareData$arrow = (_middlewareData$arrow2 = middlewareData.arrow) === null || _middlewareData$arrow2 === void 0 ? void 0 : _middlewareData$arrow2.x) !== null && _middlewareData$arrow !== void 0 ? _middlewareData$arrow : 0), "px,").concat(Math.round((_middlewareData$arrow3 = (_middlewareData$arrow4 = middlewareData.arrow) === null || _middlewareData$arrow4 === void 0 ? void 0 : _middlewareData$arrow4.y) !== null && _middlewareData$arrow3 !== void 0 ? _middlewareData$arrow3 : 0), "px)")
    };
  }, [(_middlewareData$arrow5 = middlewareData.arrow) === null || _middlewareData$arrow5 === void 0 ? void 0 : _middlewareData$arrow5.x, (_middlewareData$arrow6 = middlewareData.arrow) === null || _middlewareData$arrow6 === void 0 ? void 0 : _middlewareData$arrow6.y]);
  const arrowProps = useMemo(() => ({
    ref: arrowRef,
    style: arrowStyles
  }), [arrowStyles]);
  const targetProps = useMemo(() => _objectSpread({
    ref: referenceRef
  }, interactions.getReferenceProps()), [interactions, referenceRef]);
  const overlayProps = useMemo(() => _objectSpread({
    ref: refs.setFloating,
    style: _objectSpread(_objectSpread({}, floatingStyles), {}, {
      zIndex: theme.zIndex.dateSelector
    })
  }, interactions.getFloatingProps()), [floatingStyles, interactions, refs]);
  return {
    T,
    locale,
    minDate,
    maxDate,
    open: context.open,
    toggleOverlay,
    placement,
    arrowProps,
    targetProps,
    overlayProps,
    onFinalSelection,
    context
  };
};
export default useSharedDateLogic;
//# sourceMappingURL=useSharedDateLogic.js.map